

























































































































































import {
  computed,
  defineComponent,
  onMounted,
  ref,
  watch
} from '@nuxtjs/composition-api'
import { useNamespacedStore } from '~/compositions/store'
import { CLASSIFIED_VIEW_NS } from '~/store/modules/shared/classifieds/view/state'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'
import { useDep } from '~/compositions/dependency-container'
import CSubtitleButton from '~/components/shared/configurable/button/CSubtitleButton.vue'
import { useI18n } from '~/compositions/i18n'
import { capitalizeSentences } from '~/utils/string'
import MyAuditStatusModal from '~/components/shared/classified/audits/MyAuditStatusModal.vue'
import RequestAuditModal from '~/components/shared/classified/audits/RequestAuditModal.vue'
import { ciMakeCarcheck } from '~/icons/source/regular/make-carcheck'
import { ciAuditRequest } from '~/icons/source/regular/audit-request-alt'
import CTimelineItem from '~/components/shared/configurable/timeline/CTimelineItem.vue'
import AuditRequestService from '~/services/audits/AuditRequestService'
import { format } from '~/utils/date'
import CTimeline from '~/components/shared/configurable/timeline/CTimeline.vue'
import CPing from '~/components/shared/configurable/timeline/CPing.vue'
import { AuditRequest, AuditRequestStatusStatus } from '~/models/audit/request'
import SelfAuditButton from '~/components/shared/classified/audits/SelfAuditButton.vue'
import CAnonGuardModal from '~/components/shared/configurable/user/CAnonGuardModal.vue'
import { ciMakeBikecheck } from '~/icons/source/regular/make-bikecheck'
import { ciAuditRequestBike } from '~/icons/source/regular/audit-request-bike'

export default defineComponent({
  components: {
    CAnonGuardModal,
    CPing,
    CTimeline,
    CTimelineItem,
    RequestAuditModal,
    MyAuditStatusModal,
    CSubtitleButton,
    SelfAuditButton
  },
  props: {
    classifiedId: { type: Number, required: true }
  },
  setup() {
    const { state, getters, dispatch, commit } = useNamespacedStore(
      CLASSIFIED_VIEW_NS
    )
    const auditRequests = computed(() => {
      return state.auditRequests
    })

    const showMyAuditStatusModal = ref(false)
    const showRequestAuditModal = ref(false)
    const showAnonGuardModal = ref(false)
    const urlAuditRequest = ref<AuditRequest | null>(null)
    const { t, tc } = useI18n()
    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const isSingle = computed(() => userGetters('isSingle'))
    const isAnon = computed(() => userGetters('isAnon'))
    const isAuditsUser = computed(() => userGetters('isAuditsUser'))
    const isClassifiedOwner = computed(() => getters('isOwn'))

    const userCanManageAudits = computed<boolean>(() => {
      return getters('userIsAdminOrOwner') || isAuditsUser.value
    })
    const forBikes = computed<boolean>(() => getters('isBikeClassified'))
    const auditRequestService = useDep(AuditRequestService)

    const shouldPing = computed<boolean>(() => {
      if (myAuditRequestCurrentStatus.value?.status) {
        return (
          auditRequestService.getStatusVariant(
            myAuditRequestCurrentStatus.value.status,
            0
          ) === 'info'
        )
      }
      return false
    })

    const auditsButtonClick = () => {
      // If the user has already made an audit request, show the status modal
      if (myAuditRequest.value) {
        showMyAuditStatusModal.value = true
        return
      }

      // If the user owns the classified, whatever his type is, he can make an audit request
      if (isClassifiedOwner.value) {
        const hasAuditRequests = auditRequests.value?.length > 0

        if (hasAuditRequests) {
          showMyAuditStatusModal.value = true
        } else {
          showRequestAuditModal.value = true
        }

        return
      }

      // If the user is anonymous or single, he can't make an audit request
      if (isAnon.value || isSingle.value) {
        showAnonGuardModal.value = true
        return
      }

      // If the user can manage audits, he can only view the audit requests statuses
      if (userCanManageAudits.value) {
        showMyAuditStatusModal.value = true
        return
      }

      showRequestAuditModal.value = true
    }

    const onRequestAuditModalClose = () => {
      showRequestAuditModal.value = false
    }

    const userHasAuditRequests = computed(() => {
      return Boolean(state.audits?.userHasAuditRequests)
    })
    const minimumPrice = computed(() => {
      return state.audits?.minimumPrice
    })

    const requiresAcceptance = computed(() => {
      return !!state.audits?.requiresAcceptance
    })

    const myAuditRequest = computed<AuditRequest>(() => {
      if (
        !userCanManageAudits.value &&
        userHasAuditRequests.value &&
        state.auditRequests?.length
      ) {
        // always get the latest one
        return state.auditRequests[0]
      }
      return null
    })

    const myAuditRequestCurrentStatus = computed<AuditRequestStatusStatus | null>(
      () => {
        return myAuditRequest.value?.currentStatus || null
      }
    )

    const loadedAuditRequest = computed<AuditRequest | null>(() => {
      return myAuditRequest.value || urlAuditRequest.value
    })

    const onUpdateLoadedRequest = (updatedRequest: AuditRequest) => {
      if (myAuditRequest.value) {
        myAuditRequest.value = updatedRequest
      } else if (urlAuditRequest.value) {
        urlAuditRequest.value = updatedRequest
      }
    }

    const checkAuditsHash = () => {
      if (!process.client) {
        return
      }

      const startsWithAudit = window.location.hash.startsWith('#audit-')
      if (
        (startsWithAudit || window.location.hash === '#audits') &&
        (auditRequests.value?.length > 0 || myAuditRequest.value)
      ) {
        if (startsWithAudit) {
          const hashSplit = window.location.hash.split('-')
          if (hashSplit.length > 1) {
            const id = hashSplit[1]
            if (auditRequests.value) {
              urlAuditRequest.value = auditRequests.value.find(
                (r: AuditRequest) => r.id === parseInt(id)
              )
            }
          }
        }
        showMyAuditStatusModal.value = true
      }
    }
    watch(auditRequests, (val, oldVal) => {
      if (oldVal === null && val?.length > 0) {
        // this means we just loaded something for the first time
        checkAuditsHash()
      }
    })
    onMounted(() => {
      checkAuditsHash()
    })

    const onMyAuditsModalClose = () => {
      showMyAuditStatusModal.value = false
      urlAuditRequest.value = null
    }

    const buttonIcon = computed(() => {
      if (
        (auditRequests.value === null || auditRequests.value?.length === 0) &&
        requiresAcceptance.value
      ) {
        return forBikes.value ? ciAuditRequestBike : ciAuditRequest
      }
      return forBikes.value ? ciMakeBikecheck : ciMakeCarcheck
    })

    const buttonTitle = computed(() => {
      if (
        (userCanManageAudits.value &&
          (auditRequests.value === null ||
            auditRequests.value?.length === 0)) ||
        (!isClassifiedOwner.value &&
          (auditRequests.value === null || auditRequests.value?.length === 0) &&
          requiresAcceptance.value)
      ) {
        return null
      } else if (userCanManageAudits.value || auditRequests.value) {
        return capitalizeSentences(
          tc('audit | audits', auditRequests.value?.length)
        )
      } else if (myAuditRequest.value) {
        return t('audit')
      }

      return null
    })

    const formatDate = (dateStr: string) => {
      return format(dateStr)
    }

    const makeNewRequest = () => {
      // probably called only on a cancelled request
      showRequestAuditModal.value = true
    }

    const completedRequestsCount = computed(() => {
      if (auditRequests.value) {
        return auditRequests.value.filter(
          (a: AuditRequest) =>
            a.currentStatus.status === AuditRequestStatusStatus.AUDIT_COMPLETED
        ).length
      }
      return 0
    })

    const notCompletedRequestsCount = computed(() => {
      if (auditRequests.value) {
        return auditRequests.value.filter(
          (a: AuditRequest) =>
            a.currentStatus.status !== AuditRequestStatusStatus.AUDIT_COMPLETED
        ).length
      }
      return 0
    })

    async function handleSuccessAuditRequest() {
      await dispatch('fetchAuditRequests')
      commit('SET_AUDITS', { ...state.audits, userHasAuditRequests: true })
    }

    function onCreateRequest(auditRequest: AuditRequest) {
      setTimeout(() => {
        window.location.href = auditRequest.payment.paymentUrl!
      }, 100)

      showRequestAuditModal.value = false
    }

    return {
      onCreateRequest,
      auditRequests,
      ciMakeCarcheck,
      ciAuditRequest,
      showMyAuditStatusModal,
      showAnonGuardModal,
      userCanManageAudits,
      auditsButtonClick,
      showRequestAuditModal,
      myAuditRequest,
      onRequestAuditModalClose,
      checkAuditsHash,
      urlAuditRequest,
      loadedAuditRequest,
      onMyAuditsModalClose,
      buttonTitle,
      buttonIcon,
      auditRequestService,
      formatDate,
      myAuditRequestCurrentStatus,
      shouldPing,
      userHasAuditRequests,
      makeNewRequest,
      minimumPrice,
      completedRequestsCount,
      notCompletedRequestsCount,
      isClassifiedOwner,
      handleSuccessAuditRequest,
      requiresAcceptance,
      onUpdateLoadedRequest
    }
  }
})
